import React from 'react';
import styled from 'styled-components';

interface SiteSettings {
  company_name: string;
  company_description: string;
  total_properties: number;
  active_cities: number;
  happy_clients: number;
  years_experience: number;
  show_stats_section: boolean;
  use_real_stats: boolean;
  [key: string]: any;
}

interface GeneralSettingsProps {
  settings: SiteSettings;
  onUpdate: (updates: Partial<SiteSettings>) => void;
}

const Section = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 1.5rem;
  font-size: 1.3rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.3s;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  transition: border-color 0.3s;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const HelpText = styled.small`
  color: #666;
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  cursor: pointer;
  font-weight: 500;
  color: #333;
`;

const InfoBox = styled.div`
  background: #f0f7ff;
  border: 1px solid #d0e7ff;
  border-radius: 4px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
  color: #1a5490;
`;

const GeneralSettings: React.FC<GeneralSettingsProps> = ({ settings, onUpdate }) => {
  const handleChange = (field: string, value: string | number | boolean) => {
    onUpdate({ [field]: value });
  };

  return (
    <>
      <Section>
        <SectionTitle>📋 Informações da Empresa</SectionTitle>
        
        <FormGroup>
          <Label htmlFor="company_name">Nome da Empresa</Label>
          <Input
            id="company_name"
            type="text"
            value={settings.company_name}
            onChange={(e) => handleChange('company_name', e.target.value)}
            placeholder="Ex: ImobiPro"
          />
          <HelpText>Nome que aparecerá em todo o site</HelpText>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="company_description">Descrição da Empresa</Label>
          <TextArea
            id="company_description"
            value={settings.company_description}
            onChange={(e) => handleChange('company_description', e.target.value)}
            placeholder="Breve descrição sobre sua imobiliária..."
          />
          <HelpText>Descrição que aparece no footer e outras seções do site</HelpText>
        </FormGroup>
      </Section>

      <Section>
        <SectionTitle>📊 Estatísticas Exibidas</SectionTitle>
        
        <CheckboxContainer>
          <Checkbox
            type="checkbox"
            id="show_stats_section"
            checked={settings.show_stats_section !== false}
            onChange={(e) => handleChange('show_stats_section', e.target.checked)}
          />
          <CheckboxLabel htmlFor="show_stats_section">
            Exibir seção de estatísticas na página inicial
          </CheckboxLabel>
        </CheckboxContainer>
        
        <CheckboxContainer>
          <Checkbox
            type="checkbox"
            id="use_real_stats"
            checked={settings.use_real_stats !== false}
            onChange={(e) => handleChange('use_real_stats', e.target.checked)}
          />
          <CheckboxLabel htmlFor="use_real_stats">
            Usar dados reais do sistema (propriedades e cidades)
          </CheckboxLabel>
        </CheckboxContainer>
        
        <InfoBox>
          💡 <strong>Dica:</strong> Quando "Usar dados reais" está ativado, o sistema mostra automaticamente 
          a quantidade real de imóveis cadastrados e cidades atendidas. Os campos "Clientes Satisfeitos" 
          e "Anos de Experiência" sempre usam os valores configurados abaixo.
        </InfoBox>
        
        <StatsGrid>
          <FormGroup>
            <Label htmlFor="total_properties">
              Total de Propriedades
              {settings.use_real_stats && <small style={{ color: '#666', fontWeight: 'normal' }}> (usando dados reais)</small>}
            </Label>
            <Input
              id="total_properties"
              type="number"
              value={settings.total_properties}
              onChange={(e) => handleChange('total_properties', parseInt(e.target.value) || 0)}
              min="0"
              disabled={settings.use_real_stats}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="active_cities">
              Cidades Atendidas
              {settings.use_real_stats && <small style={{ color: '#666', fontWeight: 'normal' }}> (usando dados reais)</small>}
            </Label>
            <Input
              id="active_cities"
              type="number"
              value={settings.active_cities}
              onChange={(e) => handleChange('active_cities', parseInt(e.target.value) || 0)}
              min="0"
              disabled={settings.use_real_stats}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="happy_clients">Clientes Satisfeitos</Label>
            <Input
              id="happy_clients"
              type="number"
              value={settings.happy_clients}
              onChange={(e) => handleChange('happy_clients', parseInt(e.target.value) || 0)}
              min="0"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="years_experience">Anos de Experiência</Label>
            <Input
              id="years_experience"
              type="number"
              value={settings.years_experience}
              onChange={(e) => handleChange('years_experience', parseInt(e.target.value) || 0)}
              min="0"
              max="100"
            />
          </FormGroup>
        </StatsGrid>
      </Section>
    </>
  );
};

export default GeneralSettings;